import React, {useEffect, useRef, useState} from 'react'
import {useIntl, defineMessages} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Stack,
    HStack,
    List,
    ListItem,
    Box,
    Heading,
    useBreakpointValue,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useAddressFields from '../../components/forms/useAddressFields'
import Field from '../../components/field'
import {useAddressSuggestion, usePhoneValidation} from '../../integrations/melissa/hooks/use-melissa'
import debounce from 'lodash/debounce'
import {useOutsideClick} from '@chakra-ui/react'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

import {PlusIcon} from '../../components/icons'
/* Constants and static resources */
const inputLabelResources = defineMessages({
    addressTitle: {
        id: 'use_address_fields.label.address_title',
        defaultMessage: 'Address Label (e.g Home, Work)'
    },
    firstName: {
        id: 'use_address_fields.label.first_name',
        defaultMessage: 'First Name*'
    },
    lastName: {
        id: 'use_address_fields.label.last_name',
        defaultMessage: 'Last Name*'
    },
    address: {
        id: 'use_address_fields.label.street_address',
        defaultMessage: 'Address*'
    },
    apartment: {
        id: 'use_address_fields.label.apartment',
        defaultMessage: 'Apt / Suite'
    },
    city: {
        id: 'use_address_fields.label.town_city',
        defaultMessage: 'City*'
    },
    postcode: {
        id: 'use_address_fields.label.postal_code',
        defaultMessage: 'Zip / Postal Code*'
    },
    country: {
        id: 'use_address_fields.label.country',
        defaultMessage: 'Country'
    },
    phone: {
        id: 'use_address_fields.label.telephone',
        defaultMessage: 'Telephone*'
    },
    labelHeading: {
        id: 'address_book_fields.label_heading',
        defaultMessage: 'Label'
    },
    addressHeading: {
        id: 'address_book_fields.address_heading',
        defaultMessage: 'Address'
    },
    aptOfficeHeading: {
        id: 'address_book_fields.apt_office_heading',
        defaultMessage: 'Add app, office, etc.'
    }
})

/**
 * AddressBookFields is a React functional component that renders a dynamic address form
 * with support for address suggestions, responsive design, and custom validation messages.
 *
 * This component integrates with a form management library (such as react-hook-form)
 * and provides fields for address entry, including first name, last name, address lines,
 * city, postcode, country, and phone number. It leverages internationalization for labels
 * and error messages, and adapts its layout and field presentation based on the current
 * viewport (mobile or desktop).
 *
 * Address suggestions are fetched and displayed as the user types in the address field,
 * with debounced API calls and click-outside handling to close the suggestions dropdown.
 * The component also overrides default validation messages for improved user guidance,
 * and enforces a UK-specific phone number pattern.
 *
 * Props:
 *   @param {object} form - The form object, typically from a form management library, used for field registration and value management.
 *   @param {string} [prefix=''] - Optional prefix for form field names to support nested or namespaced forms.
 *
 * Usage:
 *   <AddressBookFields form={formInstance} prefix="shipping" />
 *
 * Features:
 *   - Responsive field rendering and styling for mobile and desktop.
 *   - Address suggestions with debounced fetching and click-outside dismissal.
 *   - Customizable and internationalized field labels and validation messages.
 *   - Melissa phone validation on blur (CA/US NANP 10-digit numbers).
 *   - Accessibility enhancements (focus management).
 */
const AddressBookFields = ({form, prefix = ''}) => {
    const isMobile = useBreakpointValue({base: true, md: false})
    const fields = useAddressFields({form, prefix})
    const intl = useIntl()
    const addressFormRef = useRef()
    const boxRef = useRef()

    // State for address1 input and suggestions
    const [address1, setAddress1] = useState('')
    const [showSuggestions, setShowSuggestions] = useState(false)
    useOutsideClick({
        ref: boxRef,
        handler: () => setShowSuggestions(false)
    })

    // Initialize the useAddressSuggestion hook
    const countryCode = form.watch('countryCode')
    const {isLoading, suggestions, fetchSuggestions} = useAddressSuggestion(address1, countryCode)
    const {fetchPhoneValidation} = usePhoneValidation()

    const config = getConfig()
    const theme = useTheme()
    const {baseStyle, formStyle, addressModal} = theme.components.AddressBook
    const [showApt, setShowApt] = useState(false)

    const {site} = useMultiSite()
    const siteId = site?.id
    const ADDRESS_SUGGESTIONS_ENABLED = getSiteEnvVarValue(config.app, 'ADDRESS_SUGGESTIONS_ENABLED', siteId) === 'true'
    const ADDRESS_SERVICES_DEBOUNCE = getSiteEnvVarValue(config.app, 'ADDRESS_SERVICES_DEBOUNCE_INTERVAL', siteId)
    const ADDRESS_SERVICES_DEBOUNCE_INTERVAL = ADDRESS_SERVICES_DEBOUNCE ? Number(ADDRESS_SERVICES_DEBOUNCE) : 300
    // Debounced function to fetch suggestions
    const debouncedFetchSuggestionsRef = useRef(
        debounce((data) => {
            if (ADDRESS_SUGGESTIONS_ENABLED) {
                const {value, shouldFetch} = data
                if (value.length >= 7) {
                    fetchSuggestions({address: value, shouldFetch})
                }
            }
        }, ADDRESS_SERVICES_DEBOUNCE_INTERVAL)
    )

    // Effect to handle address1 input changes
    useEffect(() => {
        if (address1.length >= 7) {
            debouncedFetchSuggestionsRef.current({value: address1, shouldFetch: true})
        }
    }, [address1])

    const handleAddress1Change = (e) => {
        const value = e.target.value

        setAddress1(value)

        form.setValue(fields.address1.name, value)
        form.trigger(fields.address1.name)
        setShowSuggestions(true)
    }

    const handlePhoneBlur = async (e) => {
        const value = e.target.value
        const digits = (value || '').replace(/[^\d]/g, '')

        if (digits.length !== 10) return

        const result = await fetchPhoneValidation({
            phoneNumber: value,
            countryCode,
            shouldFetch: true
        })

        const isValid = result?.phoneValidationResult?.isValidPhone

        if (!isValid) {
            form.setError(fields.phone.name, {
                type: 'manual',
                message: intl.formatMessage({
                    defaultMessage: 'Invalid phone number',
                    id: 'address.phone.invalid'
                })
            })
        } else {
            form.clearErrors(fields.phone.name)
        }
    }

    const handleSuggestionClick = (suggestion) => {
        // Populate fields with the selected suggestion
        setAddress1(suggestion.address1)
        form.setValue(fields.address1.name, suggestion.address1)
        form.setValue(fields.city.name, suggestion.city)
        form.setValue(fields.stateCode.name, suggestion.stateCode)
        form.setValue(fields.postalCode.name, suggestion.postalCode)
        setShowSuggestions(false)
    }

    /* Override the required messages because are different from default */
    fields.firstName.rules.required = intl.formatMessage({
        defaultMessage: 'Enter your first name',
        id: 'address_book_fields.error.custom_first_name_required'
    })

    fields.lastName.rules.required = intl.formatMessage({
        defaultMessage: 'Enter your last name',
        id: 'address_book_fields.error.custom_last_name_required'
    })

    fields.city.rules.required = intl.formatMessage({
        defaultMessage: 'Enter a town or city',
        id: 'address_book_fields.error.custom_city_required'
    })

    fields.city.rules.minLength = {
        value: 2,
        message: intl.formatMessage({
            defaultMessage: 'Between 2 to 50 characters are required.',
            id: 'address_book_fields.error.between_2_50'
        })
    }

    fields.postalCode.rules.required = intl.formatMessage({
        defaultMessage: 'Enter a valid zip / postal code',
        id: 'address_book_fields.error.custom_postcode_required'
    })

    fields.phone.rules.required = intl.formatMessage({
        defaultMessage: 'Enter your telephone number',
        id: 'address_book_fields.error.custom_phone_required'
    })

    // Switch props based on breakpoint
    const fieldCustomProps = (labelValue) => {
        const placeholder = isMobile ? intl.formatMessage(labelValue) : ''
        const label = isMobile ? '' : intl.formatMessage(labelValue)

        return {
            hideLabel: isMobile,
            placeholder: placeholder,
            label: label,
            variant: isMobile ? 'outline' : 'webFloatingField',
            labelProps: {
                color: 'grey.600 !important',
                fontSize: 'md',
                lineHeight: '24px'
            }
        }
    }

    return (
        <Stack spacing={4} tabIndex="0" ref={addressFormRef}>
            <Heading {...addressModal.fieldHeading} {...baseStyle.centerContent} as="h4" >{intl.formatMessage(inputLabelResources.labelHeading)}</Heading>
            <Field
                {...fields.addressTitle}
                {...fieldCustomProps(inputLabelResources.addressTitle)}
            />
            <Heading {...addressModal.fieldHeading} {...baseStyle.centerContent} as="h4" >{intl.formatMessage(inputLabelResources.addressHeading)}</Heading>
            <Field {...fields.firstName} {...fieldCustomProps(inputLabelResources.firstName)} />
            <Field {...fields.lastName} {...fieldCustomProps(inputLabelResources.lastName)} />
            <Box ref={boxRef} position={'relative'}>
                <div>
                    <Field
                        {...fields.address1}
                        {...fieldCustomProps(inputLabelResources.address)}
                        inputProps={{
                            onChange: handleAddress1Change,
                            onKeyUp: () => setShowSuggestions(true),
                            autoComplete: 'off',
                            maxLength: 50
                        }}
                    />
                </div>
                {showSuggestions && suggestions.length >= 1 && (
                    <List
                        spacing={0}
                        position={'absolute'}
                        bg={'white'}
                        w={'full'}
                        zIndex={3}
                        shadow={'dark-lg'}
                        borderColor={'gray.700'}
                        maxH={'200px'}
                        overflow={'auto'}
                    >
                        {suggestions.map((suggestion, index) => {
                            const formattedSuggestion = [
                                suggestion.address1,
                                suggestion.address2,
                                suggestion.city,
                                suggestion.countryCode,
                                suggestion.postalCode
                            ]
                                .filter(Boolean)
                                .join(', ')

                            return (
                                <ListItem
                                    key={index}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    paddingTop={{base: '14px'}}
                                    paddingBottom={{base: '14px'}}
                                    marginLeft={{base: 2}}
                                    marginRight={{base: 2}}
                                    fontSize={'sm'}
                                    borderColor={'cardDivider'}
                                    borderBottomWidth={index < suggestions.length - 1 ? '1px' : '0'}
                                    _hover={{bg: 'gray.100', cursor: 'pointer'}}
                                >
                                    {formattedSuggestion}
                                </ListItem>
                            )
                        })}
                    </List>
                )}
                {isLoading && <LoadingSpinner />}
            </Box>
            <Heading {...addressModal.optionalFieldHeading} {...baseStyle.centerContent} as="h4" onClick={() => setShowApt(!showApt)}>
                <PlusIcon width={17} height={17} />{intl.formatMessage(inputLabelResources.aptOfficeHeading)}
            </Heading>
            {showApt && (
                <Field {...fields.apartment} {...fieldCustomProps(inputLabelResources.apartment)} />
            )}
            <Field {...fields.city} {...fieldCustomProps(inputLabelResources.city)} />
            <Field
                {...fields.countryCode}
                {...fieldCustomProps(inputLabelResources.country)}
                variant={'floating'}
                inputProps={{
                    disabled: true,
                    style: {
                        padding: 0,
                    }
                }}
                formControlProps={{
                    marginTop: 4
                }}
            />
            <HStack spacing={2} width="100%" alignItems="flex-start">
                <Box flex="1">
                    <Field {...fields.stateCode} variant={'floating'}/>
                </Box>

                <Box flex="1">
                    <Field
                        {...fields.postalCode}
                        {...fieldCustomProps(inputLabelResources.postcode)}
                        inputProps={{maxLength: 10}}
                    />
                </Box>
            </HStack>
            <Field
                {...fields.phone}
                {...fieldCustomProps(inputLabelResources.phone)}
                inputProps={({onChange, value}) => ({
                    ...fields.phone.inputProps({onChange, value}),
                    onBlur: handlePhoneBlur
                })}
            />
            <Field {...fields.preferred} />
        </Stack>
    )
}

AddressBookFields.propTypes = {
    form: PropTypes.object.isRequired,
    prefix: PropTypes.string
}

export default AddressBookFields
